import React, { useState, useCallback, useEffect } from "react";
import Modal from "react-modal";
import Cropper from "react-easy-crop";
import { useDropzone } from "react-dropzone";
import getCroppedImg from "./utils/cropImage";
import { useNavigate } from "react-router-dom";

Modal.setAppElement("#root");

const ImageCropModal = ({
  isOpen,
  onClose,
  onUpload,
  Stock_id,
  Trip_Number,
  refreshComponent,
}) => {
  const navigate = useNavigate();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Reset state when modal opens or closes
  useEffect(() => {
    if (!isOpen) {
      // Clear state when modal closes
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [isOpen]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const handleUpload = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      const filetype = croppedImage.type || "image/jpeg";
      const filename = `${Date.now()}-cropped.jpeg`;

      const url = `https://api.starmarketapi.online/stock_details/get-presigned-url?filename=${encodeURIComponent(
        filename
      )}&filetype=${encodeURIComponent(filetype)}&Stock_id=${encodeURIComponent(
        Stock_id.trim()
      )}&Trip_Number=${encodeURIComponent(Trip_Number.trim())}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to get presigned URL");

      const { url: presignedUrl } = await res.json();

      const uploadRes = await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": filetype },
        body: croppedImage,
      });

      if (!uploadRes.ok) throw new Error("Upload to S3 failed");

      const finalUrl = presignedUrl.split("?")[0];
      console.log("Uploaded Image URL:", finalUrl);

      alert("Image uploaded successfully!");
      onClose();
      refreshComponent();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        },
        content: {
          position: "relative",
          inset: "auto",
          width: "600px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3)",
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }}
    >
      <h2
        style={{
          marginBottom: "10px",
          textAlign: "center",
          fontFamily: "Arial, sans-serif",
        }}
      >
        Crop Your Image
      </h2>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? "#007bff" : "#ccc"}`,
          borderRadius: "4px",
          padding: "20px",
          textAlign: "center",
          marginBottom: "10px",
          background: isDragActive ? "#e6f3ff" : "#fafafa",
          cursor: "pointer",
          fontFamily: "Arial, sans-serif",
          minHeight: "100px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <input {...getInputProps()} onChange={onFileChange} />
        {!imageSrc && (
          <p style={{ margin: 0, color: isDragActive ? "#007bff" : "#666" }}>
            {isDragActive
              ? "Drop the image here ..."
              : "Drag & drop an image here, or click to select one"}
          </p>
        )}
        {imageSrc && (
          <p style={{ margin: 0, color: "#007bff" }}>
            Image loaded. Adjust crop below.
          </p>
        )}
      </div>

      {/* Cropper */}
      {imageSrc && (
        <>
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "400px",
              background: "#333",
              borderRadius: "4px",
              marginBottom: "10px",
              overflow: "hidden",
            }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={4 / 3}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%", marginBottom: "10px" }}
          />
        </>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <button
          onClick={onClose}
          style={{
            padding: "10px 20px",
            background: "#ccc",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontFamily: "Arial, sans-serif",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleUpload}
          style={{
            padding: "10px 20px",
            background: imageSrc ? "#007bff" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: imageSrc ? "pointer" : "not-allowed",
            fontFamily: "Arial, sans-serif",
          }}
          disabled={!imageSrc}
        >
          Upload Cropped Image
        </button>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
